import { getRegistryBase, getRegistryBaseForWorkspace } from './client'
import type {
  AgentFunctionConfig,
  RegistryFunctionStatus,
  RegistryMutationResponse,
  RegistryTriggerResponse
} from './types'
import type { UpdateOptions } from '@streamnative/pulsar-admin-client-typescript'

// === Workspace-scoped Agent APIs ===

export const listAgentsForWorkspace = async (org?: string, workspaceUid?: string) => {
  const { axios, basePath } = getRegistryBaseForWorkspace(org, workspaceUid)
  return axios.get<string[]>(`${basePath}/agents`)
}

export const getAgentInfoForWorkspace = async (
  agentName: string,
  org?: string,
  workspaceUid?: string
) => {
  const { axios, basePath } = getRegistryBaseForWorkspace(org, workspaceUid)
  return axios.get<AgentFunctionConfig>(`${basePath}/agents/${agentName}`)
}

export const getAgentStatusForWorkspace = async (
  agentName: string,
  org?: string,
  workspaceUid?: string
) => {
  const { axios, basePath } = getRegistryBaseForWorkspace(org, workspaceUid)
  return axios.get<RegistryFunctionStatus>(`${basePath}/agents/${agentName}/status`)
}

export const startAgentForWorkspace = async (
  agentName: string,
  org?: string,
  workspaceUid?: string
) => {
  const { axios, basePath } = getRegistryBaseForWorkspace(org, workspaceUid)
  return axios.post<RegistryMutationResponse>(`${basePath}/agents/${agentName}:start`)
}

export const stopAgentForWorkspace = async (
  agentName: string,
  org?: string,
  workspaceUid?: string
) => {
  const { axios, basePath } = getRegistryBaseForWorkspace(org, workspaceUid)
  return axios.post<RegistryMutationResponse>(`${basePath}/agents/${agentName}:stop`)
}

export const restartAgentForWorkspace = async (
  agentName: string,
  org?: string,
  workspaceUid?: string
) => {
  const { axios, basePath } = getRegistryBaseForWorkspace(org, workspaceUid)
  return axios.post<RegistryMutationResponse>(`${basePath}/agents/${agentName}:restart`)
}

export const triggerAgentForWorkspace = async (
  agentName: string,
  org?: string,
  workspaceUid?: string
) => {
  const { axios, basePath } = getRegistryBaseForWorkspace(org, workspaceUid)
  return axios.post<RegistryTriggerResponse>(`${basePath}/agents/${agentName}:trigger`)
}

export const deleteAgentForWorkspace = async (
  agentName: string,
  org?: string,
  workspaceUid?: string
) => {
  const { axios, basePath } = getRegistryBaseForWorkspace(org, workspaceUid)
  return axios.delete<RegistryMutationResponse>(`${basePath}/agents/${agentName}`)
}

// === Agent APIs (WithDefaults) ===

export const listAgentsWithDefaults = async () => {
  const { axios, basePath } = getRegistryBase()
  return axios.get<string[]>(`${basePath}/agents`)
}

export const getAgentInfoWithDefaults = async (functionName: string) => {
  const { axios, basePath } = getRegistryBase()
  return axios.get<AgentFunctionConfig>(`${basePath}/agents/${functionName}`)
}

export const registerAgentWithDefaults = async (
  functionName: string,
  functionConfig: AgentFunctionConfig,
  data?: any,
  url?: string
) => {
  const { axios, basePath } = getRegistryBase()
  const apiUrl = `${basePath}/agents/${functionName}`
  const formData = new FormData()

  if (data) {
    formData.append('data', data)
  }
  if (url) {
    formData.append('url', url)
  }
  formData.append('functionConfig', JSON.stringify(functionConfig))

  return axios.post<RegistryMutationResponse>(apiUrl, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  })
}

export const updateAgentWithDefaults = async (
  functionName: string,
  functionConfig: AgentFunctionConfig,
  updateOptions?: UpdateOptions,
  data?: any,
  url?: string
) => {
  const { axios, basePath } = getRegistryBase()
  const apiUrl = `${basePath}/agents/${functionName}`
  const formData = new FormData()

  if (data) {
    formData.append('data', data)
  }
  if (url) {
    formData.append('url', url)
  }
  formData.append('functionConfig', JSON.stringify(functionConfig))
  if (updateOptions) {
    formData.append('updateOptions', JSON.stringify(updateOptions))
  }

  return axios.put<RegistryMutationResponse>(apiUrl, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  })
}

export const deregisterAgentWithDefaults = async (functionName: string) => {
  const { axios, basePath } = getRegistryBase()
  return axios.delete<RegistryMutationResponse>(`${basePath}/agents/${functionName}`)
}

export const getAgentStatusWithDefaults = async (functionName: string) => {
  const { axios, basePath } = getRegistryBase()
  return axios.get<RegistryFunctionStatus>(`${basePath}/agents/${functionName}/status`)
}

export const restartAgentWithDefaults = async (functionName: string) => {
  const { axios, basePath } = getRegistryBase()
  return axios.post<RegistryMutationResponse>(`${basePath}/agents/${functionName}:restart`)
}

export const startAgentWithDefaults = async (functionName: string) => {
  const { axios, basePath } = getRegistryBase()
  return axios.post<RegistryMutationResponse>(`${basePath}/agents/${functionName}:start`)
}

export const stopAgentWithDefaults = async (functionName: string) => {
  const { axios, basePath } = getRegistryBase()
  return axios.post<RegistryMutationResponse>(`${basePath}/agents/${functionName}:stop`)
}

export const triggerAgentWithDefaults = async (
  functionName: string,
  data?: string,
  sessionId?: string,
  properties?: Record<string, string>,
  dataStream?: any,
  topic?: string
) => {
  const { axios, basePath } = getRegistryBase()
  const apiUrl = `${basePath}/agents/${functionName}:trigger`
  const formData = new FormData()

  if (data) {
    formData.append('data', data)
  }
  if (sessionId) {
    formData.append('sessionId', sessionId)
  }
  if (properties) {
    formData.append('properties', JSON.stringify(properties))
  }
  if (dataStream) {
    formData.append('dataStream', dataStream)
  }
  if (topic) {
    formData.append('topic', topic)
  }

  return axios.post<RegistryTriggerResponse>(apiUrl, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  })
}
